import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PaymentEntry, getPayments } from '../../../api/paymentsApi';
import { formatLongDate } from '../../../utils/calendar';
import { DataTable, DataTableCell, DataTableHeader, DataTableHeaderCell, DataTableRow } from '../../../components/ui/DataTable';

const PAGE_SIZE = 20;

export function formatAmount(amount: number): string {
  return `${amount.toFixed(2).replace('.', ',')} zł`;
}

const PAYMENT_METHOD_LABEL_KEYS: Record<PaymentEntry['paymentMethod'], string> = {
  TEST_PAYMENT: 'payments.methodTestPayment',
};

export default function PaymentsListView() {
  const { t } = useTranslation();

  const [payments, setPayments] = useState<PaymentEntry[]>([]);
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getPayments({ page, size: PAGE_SIZE, sortOrder: 'DESC' })
      .then((res) => {
        setPayments((prev) => (page === 0 ? res.rows : [...prev, ...res.rows]));
        setTotalElements(res.totalElements);
        setTotalPages(res.totalPages);
      })
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <Box sx={(theme) => ({ p: 4, width: '100%', flex: 1, backgroundColor: theme.app.mutedBackground })}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
        <Typography variant="h4">{t('sidebar.payments')}</Typography>
        <Typography variant="h4" color="text.secondary">
          {totalElements}
        </Typography>
      </Box>

      {loading && page === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ mt: 2 }}>
          <DataTable>
            <PaymentsTableHeader />
            {payments.map((payment) => (
              <PaymentRow key={payment.id} payment={payment} />
            ))}
          </DataTable>
        </Box>
      )}

      {page + 1 < totalPages && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button onClick={() => setPage((p) => p + 1)}>{t('payments.loadMore')}</Button>
        </Box>
      )}
    </Box>
  );
}

export function PaymentsTableHeader() {
  const { t } = useTranslation();
  return (
    <DataTableHeader>
      <DataTableHeaderCell flex={2}>{t('payments.columnMethod')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('payments.columnAmount')}</DataTableHeaderCell>
      <DataTableHeaderCell>{t('payments.columnDate')}</DataTableHeaderCell>
    </DataTableHeader>
  );
}

export function PaymentRow({ payment }: { payment: PaymentEntry }) {
  const { t } = useTranslation();
  return (
    <DataTableRow>
      <DataTableCell flex={2}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t(PAYMENT_METHOD_LABEL_KEYS[payment.paymentMethod])}
        </Typography>
      </DataTableCell>
      <DataTableCell>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {formatAmount(payment.amount)}
        </Typography>
      </DataTableCell>
      <DataTableCell>
        <Typography variant="body2">{formatLongDate(payment.paymentDate)}</Typography>
      </DataTableCell>
    </DataTableRow>
  );
}
